import { useFetcher } from "~/hooks/useFetcher";
import { type AgentList, AgentScope, agentByNamePath } from "~/services/agents";

/**
 * Resolves the UUID of the SYSTEM agent with the given `name` (e.g.
 * "legacy-dataset-assistant") through an exact-name lookup.
 *
 * The dataset assistant resolves this up front so skills and the connector
 * picker know whose to fetch.
 *
 * A lookup rather than a browse scan: browse returns project agents only, and
 * this product's agent is not one.
 *
 * `agentId` is null until the lookup resolves to a SYSTEM agent. `error` is set
 * when the lookup fails (e.g. a 403 for a caller without `use:agents`) or finds
 * no SYSTEM agent with the name, so callers can tell failure from loading.
 */
export function useSystemAgentId(name: string | null): {
  agentId: string | null;
  error: Error | null;
} {
  const { data, error } = useFetcher<AgentList>(name ? agentByNamePath(name) : null);
  // A resolved id wins over `error`: SWR keeps cached data when a background
  // revalidation fails, and one blip must not unmount a live conversation.
  const agentId = data?.items.find((agent) => agent.scope === AgentScope.SYSTEM)?.id;
  if (agentId) return { agentId, error: null };
  if (error) {
    return { agentId: null, error: error instanceof Error ? error : new Error(String(error)) };
  }
  if (!data) return { agentId: null, error: null };
  return { agentId: null, error: new Error(`No SYSTEM agent is named "${name}"`) };
}
